import styled from '@emotion/styled';
import {
  palette,
  spacing,
  radius,
  shadow,
  typography,
  layout,
} from '../../../style-utils/theme';
import { down } from '../../../style-utils/media-queries';

/**
 * Styled layer for the standalone /roster page — page chrome only. The roster
 * itself is <DashboardBuildingRoster />, which brings its own styles from
 * dashboard-units.styled, so nothing here restyles a card or a row.
 *
 * Every value is a token (see design_system.md); the only literals are
 * structural.
 */

/* THE PAGE'S SCROLLER, and it was missing.
 *
 * Measured: nothing on this route could scroll. LayoutContainer is 100dvh,
 * DeskShell's <ContentWrapper> is flex:1 inside it, NavbarAndContentContainer
 * is height:100%, and ContentAndButtonsContainer is rendered with `dashboard`,
 * which is `overflow: hidden` (layout-container.styled.tsx). So the card was
 * pinned to exactly one viewport and everything past its bottom edge was
 * CLIPPED with no scrollbar anywhere to reach it — a roster longer than the
 * screen simply lost its tail, and "Visitors inside" is the section at the
 * bottom, so the open arrivals were the first thing to go.
 *
 * The fix belongs here rather than on the shared container: /dashboard already
 * owns a scroller of its own (DashboardContentContainer), and giving the
 * `dashboard` variant an overflow would put a second one inside it.
 *
 * `min-height: 0` is the other half — a flex child will not shrink below its
 * content without it, so `overflow-y` alone would never engage.
 *
 * Below md the shell stops being viewport-height (LayoutContainer goes
 * `height: auto` and both containers go `overflow: visible`), so the document
 * itself is the scroller there and this box must NOT become a second one
 * inside a page that already scrolls. */
export const PageBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  ${down('md')} {
    overflow-y: visible;
  }
`;

/* ────────────────────────  Building picker  ─────────────────────── */

// Only rendered when the user covers more than one building — nobody should be
// asked a question that has exactly one answer.
export const PickerBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.lg} ${spacing.lg} 0;
  flex-wrap: wrap;

  ${down('md')} {
    padding: ${layout.gutterMobile} ${layout.gutterMobile} 0;
  }
`;

export const PickerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 260px;
  flex: 1 1 260px;
  max-width: 420px;
`;

/* ────────────────────────  Message states  ──────────────────────── */

// Stands in for the roster when there is nothing to show it for: no permission,
// or no buildings in reach. Deliberately a calm explanation rather than a
// redirect — bouncing someone out of a tile they can see is how redirect loops
// and "the page just flashes" bugs are born.
export const MessageCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xs};
  margin: ${spacing.lg};
  padding: ${spacing.xl};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  text-align: center;

  ${down('md')} {
    margin: ${layout.gutterMobile};
    padding: ${spacing.md};
  }
`;

export const MessageTitle = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const MessageText = styled.p`
  margin: 0;
  max-width: 46ch;
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* The skeleton's gutter: the same one PickerBar and BannerWrapper use, so the
   placeholder rows sit where the roster's rows are about to. */
export const LoadingWrapper = styled.div`
  padding: ${spacing.lg} ${spacing.lg} 0;

  ${down('md')} {
    padding: ${layout.gutterMobile} ${layout.gutterMobile} 0;
  }
`;

export const BannerWrapper = styled.div`
  padding: ${spacing.lg} ${spacing.lg} 0;

  ${down('md')} {
    padding: ${layout.gutterMobile} ${layout.gutterMobile} 0;
  }
`;

/* ────────────────────────────  Footer  ──────────────────────────── */

export const FooterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg};
  flex-shrink: 0;

  ${down('md')} {
    padding: ${spacing.sm} ${layout.gutterMobile};
  }
`;
